/**
 * Business Gallery Component
 * 업체 이미지 갤러리 - 썸네일 그리드 + 라이트박스
 * 이미지와 YouTube 동영상 지원
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ImageIcon, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BusinessGalleryProps {
  images: string[];
  title?: string;
}

interface GalleryItem {
  url: string;
  type: "image" | "youtube";
  videoId?: string;
}

/**
 * YouTube URL에서 video ID 추출
 */
function extractYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#/]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * URL이 YouTube인지 확인
 */
function isYouTubeUrl(url: string): boolean {
  return /youtube\.com|youtu\.be/.test(url);
}

/**
 * YouTube 썸네일 URL 생성
 */
function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/**
 * YouTube embed URL 생성
 */
function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * 갤러리 아이템 배열로 변환
 */
function parseGalleryItems(urls: string[]): GalleryItem[] {
  return urls.map((url) => {
    if (isYouTubeUrl(url)) {
      const videoId = extractYouTubeVideoId(url);
      if (videoId) {
        return {
          url,
          type: "youtube",
          videoId,
        };
      }
    }
    return {
      url,
      type: "image",
    };
  });
}

export function BusinessGallery({ images, title = "갤러리" }: BusinessGalleryProps) {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  if (!images || images.length === 0) {
    return null;
  }

  const galleryItems = parseGalleryItems(images);

  const openLightbox = (item: GalleryItem, index: number) => {
    setSelectedItem(item);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedItem(null);
  };

  const navigateItem = (direction: "prev" | "next") => {
    if (direction === "prev") {
      const newIndex = selectedIndex > 0 ? selectedIndex - 1 : galleryItems.length - 1;
      setSelectedIndex(newIndex);
      setSelectedItem(galleryItems[newIndex]);
    } else {
      const newIndex = selectedIndex < galleryItems.length - 1 ? selectedIndex + 1 : 0;
      setSelectedIndex(newIndex);
      setSelectedItem(galleryItems[newIndex]);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {galleryItems.map((item, index) => (
              <div
                key={index}
                className="relative aspect-square cursor-pointer group overflow-hidden rounded-lg"
                onClick={() => openLightbox(item, index)}
              >
                {item.type === "youtube" && item.videoId ? (
                  <>
                    <img
                      src={getYouTubeThumbnail(item.videoId)}
                      alt={`YouTube 동영상 ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => {
                        // 썸네일 로드 실패 시 fallback
                        (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-red-600/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <Play className="w-8 h-8 text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <img
                      src={item.url}
                      alt={`${title} ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200" />
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lightbox Modal */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && closeLightbox()}>
        <DialogContent className="max-w-7xl w-full p-0 bg-black/95 border-none">
          <div className="relative">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
              onClick={closeLightbox}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Content */}
            {selectedItem && (
              <div className="relative w-full h-[80vh] flex items-center justify-center">
                {selectedItem.type === "youtube" && selectedItem.videoId ? (
                  <div className="w-full h-full p-4">
                    <div className="relative w-full h-full max-w-6xl mx-auto">
                      <iframe
                        src={`${getYouTubeEmbedUrl(selectedItem.videoId)}?autoplay=1&rel=0`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`YouTube 동영상 ${selectedIndex + 1}`}
                      />
                    </div>
                  </div>
                ) : (
                  <img
                    src={selectedItem.url}
                    alt={`${title} ${selectedIndex + 1}`}
                    className="max-w-full max-h-full object-contain"
                  />
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            {galleryItems.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
                  onClick={() => navigateItem("prev")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/20"
                  onClick={() => navigateItem("next")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </>
            )}

            {/* Counter */}
            {galleryItems.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
                {selectedIndex + 1} / {galleryItems.length}
              </div>
            )}

            {/* Thumbnail Strip */}
            {galleryItems.length > 1 && (
              <div className="absolute bottom-16 left-0 right-0 overflow-x-auto px-4 pb-4">
                <div className="flex gap-2 justify-center">
                  {galleryItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedIndex(index);
                        setSelectedItem(item);
                      }}
                      className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all relative ${
                        index === selectedIndex
                          ? "border-white scale-110"
                          : "border-transparent opacity-60 hover:opacity-100"
                      }`}
                    >
                      {item.type === "youtube" && item.videoId ? (
                        <>
                          <img
                            src={getYouTubeThumbnail(item.videoId)}
                            alt={`YouTube 썸네일 ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play className="w-4 h-4 text-white" fill="white" />
                          </div>
                        </>
                      ) : (
                        <img
                          src={item.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
